import type { WorldTheme, WorldThemeId } from '@duck/contracts';

/**
 * Per-theme ART TREATMENT (M8 "Guilds at Scale", elevated). The shared {@link WorldTheme} palette gives
 * us four base hues (floor/wall/accent/sky); this file turns those into a DISTINCT SOUL per world — the
 * floor MATERIAL (warm planks / cool panels / arcane cobble), the trim + baseboard tone, what the
 * windows look out onto (a skyline, a starfield, gothic arches, a sunlit café street), and the warmth
 * of the interior glow. Same geometry, same mechanics — four worlds that *feel* different the instant
 * you arrive (Art Director mandate: alive, warm, beautiful; consistent visual language with personality).
 *
 * Everything here is DATA + cheap colour maths. The map layer ({@link createOfficeMap}) reads it once on
 * build/re-skin (never per frame) to draw the procedural interior + exterior. No art assets (ADR-0010):
 * this is the code-authored MVP look; commissioned/AI illustration would later slot in as TEXTURES behind
 * these same shapes (a tileable floor image swapped for the procedural pattern; a painted skyline behind
 * the window frames) without touching geometry, mechanics, or the accessibility contract.
 */

/** Split a packed 0xRRGGBB into its channels. */
export function rgb(hex: number): [number, number, number] {
  return [(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff];
}
/** Pack channels back into 0xRRGGBB (clamped to byte range). */
export function pack(r: number, g: number, b: number): number {
  const c = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : Math.round(v));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}
/** Linear blend a → b by t∈[0,1] in RGB space. Cheap; only ever called on build/re-skin, never per frame. */
export function mix(a: number, b: number, t: number): number {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  return pack(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}
/** Lighten toward white by t. */
export function lighten(hex: number, t: number): number {
  return mix(hex, 0xffffff, t);
}
/** Darken toward black by t. */
export function darken(hex: number, t: number): number {
  return mix(hex, 0x000000, t);
}

/** The look of a floor's material — drives the pattern the map draws across the world floor. */
export type FloorMaterial = 'planks' | 'tiles' | 'panels' | 'cobble';
/** What the exterior windows look out onto — the "sense of place" beyond the walls. */
export type WindowScene = 'skyline' | 'starfield' | 'arches' | 'street';

/**
 * The resolved art treatment for one theme. Colours are derived from the shared palette so a future
 * palette tweak flows through automatically; the categorical choices (material/window/glow) give each
 * world its own character.
 */
export interface ThemeArt {
  /** The floor material pattern. */
  floor: FloorMaterial;
  /** Base + alternate tones for the floor pattern (planks alternate, tiles checker, panels seam, etc). */
  floorA: number;
  floorB: number;
  /** Faint grout/seam line colour between floor cells/planks. */
  floorSeam: number;
  /** The wall band drawn along the top edge (the "back wall" you see in a top-down room). */
  wall: number;
  wallHi: number;
  wallLo: number;
  /** Baseboard / skirting trim where wall meets floor. */
  baseboard: number;
  /** Warm/cool accent used for trims, rug borders, and the interaction pads. */
  accent: number;
  /** Window pane (the lit sky beyond) + frame tone. */
  windowScene: WindowScene;
  windowSky: number;
  windowFrame: number;
  /** Rug fill + border for the soft area rugs that anchor each room. */
  rug: number;
  rugBorder: number;
  /** Soft interior glow colour (lamp/lantern pools) and its strength 0..1. */
  glow: number;
  glowStrength: number;
}

/** Resolve a theme's full art treatment from its shared palette + per-world categorical choices. */
export function themeArt(theme: WorldTheme): ThemeArt {
  const { floor, wall, accent, sky } = theme.palette;
  const choice = THEME_CHOICES[theme.id];

  // Floor tones: two close variants of the theme floor (for planks/tiles/panels to alternate) plus a
  // darker seam. Kept close together so the pattern reads as TEXTURE, never as a loud checkerboard.
  const floorA = lighten(floor, 0.1);
  const floorB = darken(floor, 0.08);
  const floorSeam = darken(floor, 0.32);

  // Wall band: lift the theme wall a touch (a lit back wall) with a brighter top edge + a shadowed base.
  const wallHi = lighten(wall, 0.22);
  const wallLo = darken(wall, 0.28);
  const baseboard = darken(wall, 0.42);

  // Windows look out onto a slightly-lifted theme sky so the "outside" reads as bright relative to the
  // interior, framed in a tone that ties back to the wall trim.
  const windowSky = lighten(sky, choice.windowLift);
  const windowFrame = darken(wall, 0.12);

  // A soft area-rug tone: a SUBTLE tint toward the accent (kept low so a rug reads as a quiet floor zone,
  // never a loud yellow blob), with a NEUTRAL darker border (no gold outline — that read as clutter).
  const rug = mix(floor, accent, 0.12);
  const rugBorder = darken(rug, 0.2);

  return {
    floor: choice.floor,
    floorA,
    floorB,
    floorSeam,
    wall,
    wallHi,
    wallLo,
    baseboard,
    accent,
    windowScene: choice.windowScene,
    windowSky,
    windowFrame,
    rug,
    rugBorder,
    glow: mix(accent, 0xffffff, choice.glowWarmth),
    glowStrength: choice.glowStrength,
  };
}

/** The per-world categorical choices that give each theme its character beyond palette. */
const THEME_CHOICES: Record<
  WorldThemeId,
  {
    floor: FloorMaterial;
    windowScene: WindowScene;
    /** How much brighter the window sky is than the ambient sky (so "outside" glows). */
    windowLift: number;
    /** 0 = cool glow, 1 = warm glow (mixes the accent toward white differently per mood). */
    glowWarmth: number;
    glowStrength: number;
  }
> = {
  office: { floor: 'planks', windowScene: 'skyline', windowLift: 0.18, glowWarmth: 0.35, glowStrength: 0.55 },
  space_station: { floor: 'panels', windowScene: 'starfield', windowLift: 0.0, glowWarmth: 0.15, glowStrength: 0.7 },
  wizard_academy: { floor: 'cobble', windowScene: 'arches', windowLift: 0.22, glowWarmth: 0.55, glowStrength: 0.8 },
  cozy_cafe: { floor: 'planks', windowScene: 'street', windowLift: 0.28, glowWarmth: 0.7, glowStrength: 0.65 },
  // Castle Hearthhold (ADR-0051): honey flagstone (the cobble pattern warmed by the theme palette), tall
  // gothic stained-glass arches, and a strong WARM torch/hearth glow — the warmest world in the catalog.
  medieval_castle: { floor: 'cobble', windowScene: 'arches', windowLift: 0.2, glowWarmth: 0.9, glowStrength: 0.85 },
  // Castle Hearthhold Classic (medieval_office, the v1 12-room keep) — same honey-flagstone castle look.
  medieval_office: { floor: 'cobble', windowScene: 'arches', windowLift: 0.2, glowWarmth: 0.9, glowStrength: 0.85 },
};
